import { Glyph } from '../ui/Glyph.js';
import { ParticleLayer } from '../fx/particles/ParticleLayer.js';

/**
 * NightSleepScreen — the quietest screen in the game: players with a NONE action
 * (asleep) see near-black stillness while others act. Stillness is the point —
 * a single breathing closed-eye glyph, a whispered "You sleep.", a sliver of red
 * moonglow at the very top edge, and the faintest drift of fog. Nothing to do.
 */
export function NightSleepScreen({ caption }: { caption?: string }) {
  return (
    <div
      className="game-screen"
      style={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(180deg, #000000 0%, #0a0000 100%)',
        gap: 'var(--space-6)',
        overflow: 'hidden',
      }}
    >
      {/* A sliver of blood-moon glow bleeding down from the top edge. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '22%',
          pointerEvents: 'none',
          background:
            'radial-gradient(120% 100% at 50% -20%, rgba(153,0,0,0.28) 0%, rgba(50,0,1,0.12) 40%, transparent 72%)',
        }}
      />

      {/* Faintest fog drift (phone-tier capped; the preset itself is sparse). */}
      <ParticleLayer preset="fog" intensity={0.4} slot={0} />

      {/* Breathing closed eye — sleep, made literal, on a slow 4s loop. */}
      <div style={{ position: 'relative', zIndex: 1, animation: 'sleep-breathe 4s ease-in-out infinite' }}>
        <Glyph name="closedEye" size={52} tone="bone" />
      </div>

      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
        <p
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 24,
            color: 'rgba(255,255,255,0.4)',
            margin: 0,
            letterSpacing: '0.08em',
          }}
        >
          You sleep.
        </p>
        {caption && (
          <p
            style={{
              marginTop: 'var(--space-4)',
              color: 'rgba(255,255,255,0.22)',
              fontSize: 13,
              maxWidth: 260,
              lineHeight: 1.5,
              textAlign: 'center',
            }}
          >
            {caption}
          </p>
        )}
      </div>

      <style>{`
        @keyframes sleep-breathe {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50%       { opacity: 0.7; transform: scale(1.05); }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes sleep-breathe { 0%, 100% { opacity: 0.55; transform: none; } }
        }
      `}</style>
    </div>
  );
}
